import React, { useState } from "react";
import { FiUsers, FiBarChart2, FiCpu, FiGrid, FiGitMerge, FiGithub, FiKey, FiTarget, FiArrowRight, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { TutorialsBadge, TutorialCallout, TutorialVideoModal, DocLinkCallout } from "./Tutorials";
import { getTutorialsForAction, getDocLinkForAction } from "../constants/tutorials";
import "./AdminHome.css";

export const MODULES = [
  {
    id: "users",
    title: "User & Org & Broker Dash",
    icon: FiUsers,
    iconClass: "users",
    description:
      "Create and manage user accounts, view existing users, maintain organization branding assets, and run Broker Dash operations such as forwarding emails.",
    actions: [
      { id: "adduser", label: "Add User" },
      { id: "userslist", label: "Users List" },
      { id: "userInvoices", label: "User Invoices" },
      { id: "brokerDashOperations", label: "Broker Dash Operations" },
      { id: "upload", label: "Upload Organization Icon" },
    ],
  },
  {
    id: "dashboards",
    title: "Analytics & Dashboards",
    icon: FiBarChart2,
    iconClass: "dashboards",
    description:
      "View reports, analytics, KPIs, and operational insights from the platform.",
    actions: [
      { id: "dashboards", label: "Manage Dashboards" },
      { id: "dashboardSummariser", label: "Dashboard Summariser" },
      { id: "orgDashboardVisibility", label: "Revoke Own Org Visibility" },
      { id: "dashboardSkillGuide", label: "Dashboard Skill Setup For New Repositories" },
      { id: "dashboardSkillExistingGuide", label: "Dashboard Skill Setup For Existing Repositories" },
      { id: "munshotDataApiDocs", label: "Munshot Data Tools API Docs" },
    ],
  },
  {
    id: "agents",
    title: "Agent Management",
    icon: FiCpu,
    iconClass: "agents",
    description:
      "Configure agents, update agent settings, manage reusable agent libraries, and organize agent categories.",
    actions: [
      { id: "create", label: "Create New Agent" },
      { id: "edit", label: "Edit Agent" },
      { id: "modify", label: "Modify Library Agents" },
      { id: "agentCategoryModify", label: "Modify Agent Categories" },
    ],
  },
  {
    id: "sheets",
    title: "Sheets",
    icon: FiGrid,
    iconClass: "sheets",
    description:
      "Promote workbooks into reusable sheet templates, scope them by organization, and manage global defaults for users.",
    actions: [
      { id: "sheetsTemplates", label: "Manage Sheet Templates" },
    ],
  },
  {
    id: "workflows",
    title: "Workflow Templates",
    icon: FiGitMerge,
    iconClass: "agents",
    description:
      "Build and manage Research DAG templates — define agent nodes, research areas, synthesizer, and dependency edges.",
    actions: [
      { id: "workflowTemplates", label: "Manage Workflow Templates" },
    ],
  },
  {
    id: "platform",
    title: "Secrets Management",
    icon: FiKey,
    iconClass: "platform",
    directAction: "credentialManager",
    description:
      "List secret metadata and create or replace write-only values directly in Infisical without reading existing values.",
    actions: [],
  },
  {
    id: "github-actions",
    title: "Munshot Actions",
    icon: FiGithub,
    iconClass: "github",
    directAction: "githubActions",
    description:
      "Run compatible workflow YAML on Munshot infrastructure for private repositories, with secrets, caches, logs, and artifacts under your control.",
    actions: [],
  },
  {
    id: "client-finder",
    title: "Client Finder",
    icon: FiTarget,
    iconClass: "clientfinder",
    directAction: "clientFinder",
    description:
      "Upload an organization's shared company list (Excel or CSV) for Client Finder, and download the company list template.",
    actions: [],
  },
];

export const getModuleById = (moduleId) => MODULES.find((m) => m.id === moduleId);

export const getActionLabel = (componentId) => {
  for (const mod of MODULES) {
    if (mod.directAction === componentId) return null;
    const action = mod.actions.find((a) => a.id === componentId);
    if (action) return action.label;
  }
  return null;
};

const AdminHome = ({ onSelectModule }) => (
  <div className="admin-home">
    <TutorialsBadge />
    <header className="admin-home__hero">
      <span className="admin-home__eyebrow">Administration Console</span>
      <h1 className="admin-home__title">Muns.ai Admin Panel</h1>
      <p className="admin-home__subtitle">Select a module to continue.</p>
    </header>

    <div className="admin-home__grid">
      {MODULES.map((mod) => {
        const Icon = mod.icon;
        return (
          <button
            key={mod.id}
            type="button"
            className="admin-home__card"
            onClick={() => onSelectModule(mod.id)}
          >
            <span className={`admin-home__card-icon admin-home__card-icon--${mod.iconClass}`}>
              <Icon aria-hidden="true" />
            </span>
            <h2 className="admin-home__card-title">{mod.title}</h2>
            <p className="admin-home__card-desc">{mod.description}</p>
            <span className="admin-home__card-cta">
              Open module <FiArrowRight aria-hidden="true" />
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

export const ModuleView = ({ module, onBack, onSelectAction }) => {
  const Icon = module.icon;
  const [playing, setPlaying] = useState(null);

  return (
    <div className="admin-module">
      <button type="button" className="admin-module__back" onClick={onBack}>
        <FiChevronLeft aria-hidden="true" />
        Back to modules
      </button>

      <header className="admin-module__header">
        <div className="admin-module__icon-row">
          <span className={`admin-module__icon admin-module__icon--${module.iconClass}`}>
            <Icon aria-hidden="true" />
          </span>
          <h1 className="admin-module__title">{module.title}</h1>
        </div>
        <p className="admin-module__desc">{module.description}</p>
      </header>

      <div className="admin-module__actions" role="list">
        {module.actions.map((action) => {
          const tutorials = getTutorialsForAction(action.id);
          const docLink = getDocLinkForAction(action.id);
          const highlighted = tutorials.length > 0 || Boolean(docLink);
          const button = (
            <button
              key={action.id}
              type="button"
              className={`admin-module__action${highlighted ? " admin-module__action--tutorial" : ""}`}
              role={highlighted ? undefined : "listitem"}
              onClick={() => onSelectAction(action.id)}
            >
              <span className="admin-module__action-label">{action.label}</span>
              <FiChevronRight className="admin-module__action-arrow" aria-hidden="true" />
            </button>
          );
          if (!highlighted) return button;
          return (
            <div key={action.id} className="admin-module__action-row" role="listitem">
              {button}
              <div className="admin-module__callouts">
                {tutorials.map((tutorial, index) => (
                  <TutorialCallout
                    key={tutorial.id}
                    tutorial={tutorial}
                    onPlay={setPlaying}
                    label={tutorials.length > 1 ? `Tutorial ${index + 1}` : undefined}
                  />
                ))}
                {docLink && (
                  <DocLinkCallout
                    docLink={docLink}
                    onOpen={(link) => onSelectAction(link.targetAction, { anchor: link.anchor })}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <TutorialVideoModal tutorial={playing} onClose={() => setPlaying(null)} />
    </div>
  );
};

export const AdminBreadcrumb = ({ module, actionLabel, onHome, onModule }) => (
  <nav className="admin-breadcrumb" aria-label="Breadcrumb">
    <button type="button" className="admin-breadcrumb__link" onClick={onHome}>
      Home
    </button>
    <span className="admin-breadcrumb__sep" aria-hidden="true">
      /
    </span>
    <button type="button" className="admin-breadcrumb__link" onClick={onModule}>
      {module.title}
    </button>
    {actionLabel && (
      <>
        <span className="admin-breadcrumb__sep" aria-hidden="true">
          /
        </span>
        <span className="admin-breadcrumb__current" aria-current="page">
          {actionLabel}
        </span>
      </>
    )}
  </nav>
);

export default AdminHome;
